import { useLocation, useNavigate } from "react-router-dom";
import httpService from "services/http-service";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Loading from "react-fullscreen-loading";
import user from "assets/img/icons/user.png";
import { calculateAge } from "utils/utils";
import { FaInstagram, FaLinkedin, FaArrowLeft, FaTimes } from "react-icons/fa";
import { EventUserInteractionType } from "utils/utils";
import { getLocalStorageValue } from "utils/utils";
import { getLocalStorageKey } from "utils/utils";
import { Dialog, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import config from "utils/config";
import logo from "assets/img/logo/logo.png";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

// Semantic accent per interest — keeps the meaning of the tag readable at a glance
// instead of a single generic brand color repeated everywhere.
const INTEREST_STYLES = {
  friends: { label: "🎉 Friendships", type: EventUserInteractionType?.MakeFriends },
  network: { label: "🤝 Networking", type: EventUserInteractionType?.Network },
  date: { label: "❤️ Dating", type: EventUserInteractionType?.Date },
};

// Best-effort icon lookup for the free-text interest tags coming from
// userStatusDetails, so pills read like "🎵 Arts, Culture and Entertainment"
// instead of plain text. Falls back to a neutral star if nothing matches.
const TAG_ICONS = [
  [/network/i, "🤝"],
  [/art|culture|entertainment/i, "🎵"],
  [/coffee|food/i, "☕"],
  [/travel/i, "✈️"],
  [/fitness|wellness|gym/i, "🏋️"],
  [/startup|business|entrepreneur/i, "💡"],
  [/tech/i, "💻"],
  [/conversation/i, "🗣️"],
  [/date|dating/i, "❤️"],
  [/friend/i, "🎉"],
  [/music/i, "🎧"],
  [/book|read/i, "📚"],
  [/outdoor|hik|nature/i, "🌿"],
];

const getTagIcon = (text = "") => {
  const match = TAG_ICONS.find(([pattern]) => pattern.test(text));
  return match ? match[1] : "⭐";
};

export default function UserProfile({ selectedUserId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  const [userData, setUserData] = useState(null);
  const [reactionClicked, setReactionClicked] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState("");
  const [userId, setUserId] = useState();
  const [isSaved, setIsSaved] = useState(false);
  const [isSharedProfile, setIsSharedProfile] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const paramUserId = urlParams.get("userId") || urlParams.get("userid");
    if (paramUserId) {
      setIsSharedProfile(true);
      if (!getLocalStorageKey("userEmail")) {
        promptEmailAndLogin();
      }
      localStorage.setItem("selectedUserId", paramUserId);
      setUserId(paramUserId);
      getUserProfile(paramUserId);
    }
  }, [location]);

  useEffect(() => {
    if (selectedUserId) {
      setUserId(selectedUserId);
    }
  }, [selectedUserId]);

  const handleReactionClickWithConfirmation = (eventType) => {
    setSelectedEventType(eventType);
    confirmAction(eventType);
  };

  const openDialog = (eventType) => {
    setSelectedEventType(eventType);
    setDialogOpen(true);
  };
  const closeDialog = () => {
    setDialogOpen(false);
  };

  const confirmAction = (eventType) => {
    handleReactionClick(eventType || selectedEventType);
    closeDialog();
  };

  const handleReactionClick = async (eventType) => {
    const eventId = getLocalStorageKey("eventId");
    const userId = getLocalStorageValue("userDetails", "userId");

    const eventInteractionData = {
      id: 0,
      eventId: eventId,
      swipingUser: userId,
      userSwipedOn: selectedUserId,
      type: eventType,
    };
    setLoading(true);
    const endpoint = `/EventUserInteraction`;
    const response = await httpService("POST", endpoint, eventInteractionData);
    setLoading(false);
    if (!response) {
      toast.error("Something went wrong");
    } else {
      toast.success("We let them know that you've made reaction of " + eventType);
    }

    setReactionClicked(true);
  };

  const handleSaveProfile = async () => {
    const eventId = getLocalStorageKey("eventId");
    const saveData = {
      userId: selectedUserId,
      eventId: eventId,
      isSavedUser: !isSaved,
    };

    setLoading(true);
    const endpoint = `/User/SavedUser`;
    const response = await httpService("POST", endpoint, saveData);
    setLoading(false);

    if (response) {
      setIsSaved(!isSaved);
      toast.success(isSaved ? "Profile unsaved" : "Profile saved");
    } else {
      toast.error("Something went wrong");
    }
  };

  const handleShareProfile = async () => {
    const profileUrl = `${config.SPOTS_BASE_URL}/auth/user-profile?userId=${userData?.id}`;
    const shareText = `Hey, you might be interested in this person`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: "Check out this profile",
          text: shareText,
          url: profileUrl,
        });
      } else if (navigator.clipboard) {
        const fallbackText = `${shareText}: ${profileUrl}`;
        await navigator.clipboard.writeText(fallbackText);
        toast.success("Profile link copied to clipboard", {
          position: "bottom-center",
        });
        window.open(`sms:?&body=${encodeURIComponent(fallbackText)}`);
      } else {
        const fallbackText = `${shareText}: ${profileUrl}`;
        await navigator.clipboard.writeText(fallbackText);
        toast.success("Profile link copied to clipboard", {
          position: "bottom-center",
        });
      }
    } catch (err) {
      const fallbackText = `${shareText}: ${profileUrl}`;
      await navigator.clipboard.writeText(fallbackText);
      toast.success("Profile link copied to clipboard", {
        position: "bottom-center",
      });
    }
  };

  const shouldHideDateChip = () => {
    const eventDataKeys = Object.keys(sessionStorage).filter((key) => key.startsWith("eventData_"));

    return eventDataKeys.some((key) => {
      const eventData = JSON.parse(sessionStorage.getItem(key));
      return eventData?.vibe === "Networking";
    });
  };

  useEffect(() => {
    if (selectedUserId || userId) {
      getUserProfile(selectedUserId || userId);
    } else {
      const userId = localStorage.getItem("selectedUserId");
      if (userId) {
        getUserProfile(userId);
      } else {
        toast.error("No user found");
      }
    }
  }, []);

  const getUserProfile = async (id) => {
    setLoading(true);

    const endpoint = `/User/${id}`;
    const response = await httpService("GET", endpoint);

    setLoading(false);

    if (response?.Error === "HttpException") {
      return response;
    } else {
      setUserData(response);

      const saved = response?.savedUsers?.some((user) => user?.isSavedUser === true);
      setIsSaved(!!saved);
    }
  };

  const handleLinkClick = (link, platform) => {
    if (!link) {
      toast.info(`No ${platform} was found for this user`);
    } else {
      window.open(link, "_blank");
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const promptEmailAndLogin = async () => {
    const { value: email } = await MySwal.fire({
      title: "Enter your email",
      input: "email",
      inputLabel: "Email address",
      inputPlaceholder: "Enter your email address",
      inputValidator: (value) => {
        if (!value) {
          return "You need to write your email!";
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return "Please enter a valid email address!";
        }
        return null;
      },
      showCancelButton: false,
      confirmButtonColor: "black",
      confirmButtonText: "Submit",
      cancelButtonText: "Cancel",
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    if (email) {
      await handleLogin(email);
    }
  };

  const handleLogin = async (email = "") => {
    if (email) {
      setLoading(true);
      localStorage.setItem("userEmail", JSON.stringify(email));
      const endpoint = `Verification/EmailVerificationCode/${email}`;
      const response = await httpService("POST", endpoint, {});
      if (!response) {
        setLoading(false);
        toast.error(response.message || "Error Logging in");
      } else {
        if (response) {
          setLoading(false);
          localStorage.setItem("userDetails", JSON.stringify(response));
        }
      }
    }
  };

  // The one active category (network / date / makeFriends) is treated as the
  // "primary" pill and gets the highlighted tan treatment, matching the
  // reference design's single accented chip.
  const primaryTag = userData?.network
    ? INTEREST_STYLES.network
    : userData?.date
    ? INTEREST_STYLES.date
    : userData?.makeFriends
    ? INTEREST_STYLES.friends
    : null;

  const hasInterests =
    !!primaryTag || (userData?.userStatusDetails?.length ?? 0) > 0;

  return (
    <>
      <div className="flex h-full w-full flex-col items-center bg-white dark:bg-[#18151A] pb-8">
        <Loading loading={loading} loaderColor="#252424" />

        <div className="w-full max-w-full md:px-0 xl:max-w-[420px]">
          {/* Top bar: back / logo / close, exactly like the reference */}
      

          <div className="px-4">
            {/* Photo card */}
            <div className="relative mt-3 overflow-hidden rounded-[28px] shadow-[0_18px_40px_-18px_rgba(0,0,0,0.35)]">
              <img
                className="h-[18.2rem] w-full object-cover"
                src={(userData?.userProfilePhotos && userData?.userProfilePhotos[0]?.photoUrl) || user}
                alt="user"
                style={{ objectPosition: "center top" }}
              />
            </div>

            {/* Name row + social icons */}
            <div className="mt-4 flex items-start justify-between">
              <div>
                <div className="flex items-baseline gap-2">
                  <h1 className="text-[28px] font-bold leading-tight text-[#1A1A1A] dark:text-white">
                    {userData?.firstName}
                  </h1>
                  {userData?.dateOfBirth && (
                    <span className="text-xl font-medium text-gray-400">{calculateAge(userData?.dateOfBirth)}</span>
                  )}
                </div>
                <p className="text-base font-medium text-gray-400">
                  {[userData?.sex, userData?.title].filter(Boolean).join(" · ")}
                </p>
              </div>

              <div className="flex shrink-0 gap-2 pt-1">
                {userData?.instagram && (
                <button
                  type="button"
                  onClick={() => handleLinkClick("https://instagram.com/" + userData?.instagram, "Instagram")}
                  aria-label="Instagram"
                  className="flex h-11 w-11 items-center justify-center rounded-full text-white shadow-sm transition active:scale-95"
                  style={{ background: "linear-gradient(45deg, #f9ce34, #ee2a7b, #6228d7)" }}
                >
                  <FaInstagram className="text-lg" />
                </button>
                )}
                {userData?.linkedIn && (
                <button
                  type="button"
                  onClick={() => handleLinkClick(userData?.linkedIn, "LinkedIn")}
                  aria-label="LinkedIn"
                  className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0A66C2] text-white shadow-sm transition active:scale-95"
                >
                  <FaLinkedin className="text-lg" />
                </button>
                )}
              </div>
            </div>

            {/* Interests */}
            
            <div className="mt-6">
              <h5 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-800 dark:text-gray-500">
                Interested in
              </h5>
               {hasInterests ? (
                <div className="flex flex-wrap gap-2.5">
                {primaryTag && (
                  <span className="flex items-center gap-2 rounded-full bg-[#F5E7C6] py-1.5 pl-1.5 pr-4 text-sm font-semibold text-[#5B4A22]">
                    <span
                      className="flex h-7 w-7 items-center justify-center rounded-full text-base"
                      style={{ background: "linear-gradient(135deg, #F4A65A, #EF7C3E)" }}
                    >
                      {primaryTag.label.split(" ")[0]}
                    </span>
                    {primaryTag.label.replace(/^\S+\s/, "")}
                  </span>
                )}
                {userData?.userStatusDetails?.map((detail) => (
                  <span
                    key={detail?.id}
                    className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-[#252424] dark:border-white/15 dark:bg-white/5 dark:text-white"
                  >
                    <span className="text-base leading-none">{getTagIcon(detail?.choice)}</span>
                    {detail?.choice}
                  </span>
                ))}
              </div>
              ) : (
  <p className="text-sm text-gray-500 dark:text-gray-400">
    This user hasn’t added any interests yet
  </p>
)}
              {/* <div className="flex flex-wrap gap-2.5">
                {primaryTag && (
                  <span className="flex items-center gap-2 rounded-full bg-[#F5E7C6] py-1.5 pl-1.5 pr-4 text-sm font-semibold text-[#5B4A22]">
                    <span
                      className="flex h-7 w-7 items-center justify-center rounded-full text-base"
                      style={{ background: "linear-gradient(135deg, #F4A65A, #EF7C3E)" }}
                    >
                      {primaryTag.label.split(" ")[0]}
                    </span>
                    {primaryTag.label.replace(/^\S+\s/, "")}
                  </span>
                )}
                {userData?.userStatusDetails?.map((detail) => (
                  <span
                    key={detail?.id}
                    className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-[#252424] dark:border-white/15 dark:bg-white/5 dark:text-white"
                  >
                    <span className="text-base leading-none">{getTagIcon(detail?.choice)}</span>
                    {detail?.choice}
                  </span>
                ))}
              </div> */}
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{ style: { borderRadius: 20, padding: 4 } }}
      >
        <DialogTitle id="alert-dialog-title" className="font-bold">
          Connection request
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">Tap to let them know you want to</DialogContentText>

          <div className="mt-4 flex flex-col gap-2">
            {!shouldHideDateChip() && (
              <button
                type="button"
                onClick={() => handleReactionClickWithConfirmation(EventUserInteractionType.Date)}
                className="rounded-2xl border border-gray-200 px-4 py-3 text-left text-sm font-medium text-[#252424] transition hover:border-[#EF7C3E] hover:bg-[#FFF7EE]"
              >
                ❤️ Date
              </button>
            )}
            <button
              type="button"
              onClick={() => handleReactionClickWithConfirmation(EventUserInteractionType.Network)}
              className="rounded-2xl border border-gray-200 px-4 py-3 text-left text-sm font-medium text-[#252424] transition hover:border-[#EF7C3E] hover:bg-[#FFF7EE]"
            >
              🤝 Network
            </button>
            <button
              type="button"
              onClick={() => handleReactionClickWithConfirmation(EventUserInteractionType.MakeFriends)}
              className="rounded-2xl border border-gray-200 px-4 py-3 text-left text-sm font-medium text-[#252424] transition hover:border-[#EF7C3E] hover:bg-[#FFF7EE]"
            >
              🎉 Be Friends
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}